import { Alert, LoadingPanel } from '@dispatch/ui';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { apiRequest, errorMessage } from './api.js';
import type { DeviceRecord } from './types.js';

interface Props {
  businessId: string;
  csrfToken: string;
  supportAccessId?: string | undefined;
}

export function DevicesPanel({ businessId, csrfToken, supportAccessId }: Props) {
  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [label, setLabel] = useState('Office browser');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string>();
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();

  async function load(signal?: AbortSignal) {
    try {
      const result = await apiRequest<{ devices: DeviceRecord[] }>(
        `/api/v1/businesses/${businessId}/devices`,
        { supportAccessId, ...(signal ? { signal } : {}) },
      );
      setDevices(result.devices);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return;
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    void load(controller.signal);
    return () => controller.abort();
  }, [businessId, supportAccessId]);

  async function register(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyId('new');
    setError(undefined);
    setMessage(undefined);
    try {
      await apiRequest(`/api/v1/businesses/${businessId}/devices`, {
        method: 'POST',
        csrfToken,
        body: { label, kind: 'browser', localUnlockMethod: 'unknown' },
      });
      setMessage('The browser was registered for this business.');
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusyId(undefined);
    }
  }

  async function revoke(device: DeviceRecord) {
    setBusyId(device.id);
    setError(undefined);
    setMessage(undefined);
    try {
      await apiRequest(`/api/v1/businesses/${businessId}/devices/${device.id}`, {
        method: 'DELETE',
        csrfToken,
        supportAccessId,
      });
      setMessage(`${device.label} was revoked.`);
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusyId(undefined);
    }
  }

  if (loading) return <LoadingPanel message="Loading registered devices…" />;

  return (
    <div>
      {error ? <Alert title="Device action failed">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}

      {!supportAccessId ? (
        <section className="panel" aria-labelledby="register-browser-heading">
          <h2 id="register-browser-heading">Register this browser</h2>
          <p className="lead">
            Office browser registration creates an auditable device record. It does not convert the
            four-hour office session into a 30-day field session.
          </p>
          <form className="form-grid" onSubmit={(event) => void register(event)}>
            <div className="form-field">
              <label htmlFor="device-label">Device label</label>
              <input
                id="device-label"
                required
                maxLength={120}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            </div>
            <div className="actions">
              <button type="submit" disabled={busyId === 'new'}>
                {busyId === 'new' ? 'Registering…' : 'Register browser'}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="panel" aria-labelledby="registered-devices-heading">
        <h2 id="registered-devices-heading">Registered devices</h2>
        {devices.length === 0 ? <p>No registered devices were returned.</p> : null}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Device</th>
                <th>User</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.id}>
                  <td>{device.label}</td>
                  <td>
                    {device.display_name}
                    <br />
                    <span className="help-text">{device.email}</span>
                  </td>
                  <td>{device.device_kind}</td>
                  <td>
                    <span
                      className={`badge ${
                        device.trust_state === 'active' ? 'badge--success' : 'badge--danger'
                      }`}
                    >
                      {device.trust_state}
                    </span>
                  </td>
                  <td>
                    {device.last_active_at
                      ? new Date(device.last_active_at).toLocaleString()
                      : 'Not recorded'}
                  </td>
                  <td>
                    {device.trust_state === 'active' ? (
                      <button
                        type="button"
                        className="danger"
                        disabled={busyId === device.id}
                        onClick={() => void revoke(device)}
                      >
                        {busyId === device.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    ) : (
                      'Revoked'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
